import dayjs from "dayjs";
import type { ComputeFilterOption } from "../types";

export const formatDuration = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined) return "-";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
  return `${(ms / 3600000).toFixed(1)}h`;
};

export const formatDateString = (date: Date): string => {
  return dayjs(date).format("YYYY-MM-DD");
};

/** Resolved Compute-column label for both the unified queries list and
 *  the detail-page Compute metric. The backend composes ``compute_display``
 *  in SQL (priority cascade: warehouse name → warehouse id → cluster
 *  name → cluster id → ``DLT Pipeline: <name|id>`` → ``Job: <id>`` →
 *  ``-``) so the two surfaces can't drift. The local cascade is the
 *  safety net for transitional responses (e.g. cached payloads that
 *  predate the new field). The Job branch shows the bare id — the BE no
 *  longer joins the job inventory to resolve names. */
export interface DatabricksComputeFields {
  compute_display?: string | null;
  warehouse_name?: string | null;
  warehouse_id?: string | null;
  cluster_name?: string | null;
  cluster_id?: string | null;
  pipeline_name?: string | null;
  pipeline_id?: string | null;
  job_id?: string | null;
}

export const formatDatabricksCompute = (q: DatabricksComputeFields): string =>
  q.compute_display ||
  q.warehouse_name ||
  q.warehouse_id ||
  q.cluster_name ||
  q.cluster_id ||
  (q.pipeline_id ? `DLT Pipeline: ${q.pipeline_name || q.pipeline_id}` : "") ||
  (q.job_id ? `Job: ${q.job_id}` : "") ||
  "-";

/** One entry from the BE's unified Compute dropdown. ``kind`` is tied
 *  to the Zod schema's literal union so the helper can't be called
 *  with an unknown kind from any new caller — the existing call site
 *  already feeds a Zod-validated array, and tightening here means
 *  future internal consumers also benefit from the same exhaustiveness
 *  check at compile time. */
export interface ComputeOptionInput {
  kind: ComputeFilterOption["kind"];
  id: string;
  display: string;
}

export interface ComputeFilterOptionRendered {
  label: string;
  value: string;
}

/** Maps the BE's ``computes`` list into the dropdown's
 *  ``{label, value}`` shape and disambiguates same-display collisions.
 *
 *  - ``value`` is the prefixed ``{kind}:{id}`` string the BE's
 *    ``compute_id`` param expects (always unique by construction).
 *  - ``label`` is normally just ``c.display`` (matches the table cell
 *    rendering verbatim). When two compute resources share the same
 *    display string — e.g. two warehouses both named "Analytics
 *    Warehouse" with different ``warehouse_id``s in the inventory —
 *    every colliding entry gets a ``(<raw resource id>)`` suffix so
 *    the user can tell them apart. The disambiguator is the raw BE id
 *    (``warehouse_id`` / ``cluster_id`` / ``pipeline_id`` as it lives
 *    in ClickHouse), NOT the prefixed compute_id we send back —
 *    that's always unique and so wouldn't help a human pick the right
 *    one.
 *
 *  A second pass catches the residual edge cases the simple suffix
 *  doesn't cover: a resource literally NAMED ``Analytics (wh-1)``
 *  that collides with the suffixed form of two other ``Analytics``
 *  entries, OR a cross-kind same-display + same-id pair
 *  (``warehouse:x`` + ``cluster:x`` both named ``Foo`` → both first-pass
 *  to ``Foo (x)``). Any remaining duplicate label falls back to the
 *  ``{kind}:{id}`` suffix, which is unique by construction and matches
 *  the BE ``compute_id`` value verbatim so the disambiguator
 *  identifies exactly the row the user is selecting. */
export const buildComputeFilterOptions = (
  computes: readonly ComputeOptionInput[]
): ComputeFilterOptionRendered[] => {
  // ``Map`` instead of a plain object so externally-supplied display
  // strings (``__proto__``, ``constructor``, ``toString`` …) can't
  // poison the counter via prototype-chain collisions. Display names
  // are user-controlled data so this is real defense, not cosmetic.
  const displayCounts = new Map<string, number>();
  for (const c of computes) {
    displayCounts.set(c.display, (displayCounts.get(c.display) ?? 0) + 1);
  }
  const candidates = computes.map(c => ({
    label: (displayCounts.get(c.display) ?? 0) > 1 ? `${c.display} (${c.id})` : c.display,
    fallback: `${c.display} (${c.kind}:${c.id})`,
    value: `${c.kind}:${c.id}`,
  }));
  // Second-pass collision check — if any first-pass label still
  // collides, escalate that row to the ``(kind:id)`` fallback (unique
  // by construction). Only the colliding rows escalate; the rest keep
  // the cleaner ``(id)`` suffix.
  const labelCounts = new Map<string, number>();
  for (const c of candidates) {
    labelCounts.set(c.label, (labelCounts.get(c.label) ?? 0) + 1);
  }
  return candidates.map(c => ({
    label: (labelCounts.get(c.label) ?? 0) > 1 ? c.fallback : c.label,
    value: c.value,
  }));
};
